/**
 * 2FA gate for the session-replay viewer.
 *
 * Replays are unmasked recordings of patient charts, so "is this account a dev"
 * is a weaker answer than it looks: a dev's laptop left unlocked, or a session
 * cookie lifted from one, is enough.
 *
 * The user types an authenticator code once. It is exchanged for a random
 * server-issued token (/v1/admin/replay/unlock) that lasts 12 hours; the token
 * is kept in localStorage and sent with every replay admin request, and checked
 * server-side each time.
 *
 * So this component is not deciding anything — it is holding a credential.
 * Clearing it out of the bundle gets you nothing, because the endpoints reject a
 * request without a valid token regardless of what the UI rendered.
 *
 * Why a token and not the code itself: accepting the raw TOTP for 12 hours means
 * accepting ~2881 of the 10^6 possible codes at any moment, i.e. ~350 guesses.
 * A 256-bit token has nothing to guess and revoking it is deleting one row.
 */

import { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Container, Form, FormGroup, Input, Label } from 'reactstrap';
import Header from 'components/Headers/Header';
import { GlobalContext } from '../../api/GlobalContext';

const STORAGE_KEY = 'replay_viewer_token';

/**
 * Read the stored token, if it belongs to THIS user.
 *
 * The email is part of the record so a token cannot survive a logout and an
 * unrelated login on the same browser. No expiry is kept here on purpose: the
 * server owns the TTL, and a client-side clock would only ever disagree with it.
 */
export function readStoredCode(email, maxAgeMs = null) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!record || record.email !== email || !record.token) return null;
    if (maxAgeMs && (!record.storedAt || Date.now() - record.storedAt >= maxAgeMs)) return null;
    return record.token;
  } catch (e) {
    // Corrupt or unreadable (private mode, quota, hand-edited) reads as "no
    // code" rather than throwing a viewer that then cannot be opened.
    return null;
  }
}

export function writeStoredCode(email, token) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, token, storedAt: Date.now() }));
  } catch (e) {
    // Storage unavailable — this session still works, the user is just asked
    // again on the next page load.
  }
}

export function clearStoredCode() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* nothing to do */
  }
}

/** Wraps the viewer; renders children only once the server has accepted a code. */
const ReplayAccessGate = ({ children }) => {
  const { apiClient } = useContext(GlobalContext);
  const email = apiClient?.sessionEmail || apiClient?.email || null;

  const [unlocked, setUnlocked] = useState(false);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');

  /**
   * `isDev` arrives with the profile, one request after this mounts, and
   * apiClient is a plain object — nothing re-renders this component when the
   * flag flips. Opening a replay link in a fresh tab therefore lands here
   * before the answer exists, so "not known yet" (null) is its own state:
   * deciding on a not-yet-loaded flag denies a dev until they navigate away
   * and back.
   */
  const [isDev, setIsDev] = useState(() => (apiClient?.isDev ? true : null));

  useEffect(() => {
    let cancelled = false;
    const settle = () => {
      if (!cancelled) setIsDev(Boolean(apiClient?.isDev));
    };
    // Re-settled by any later profile load as well, not just this one: a stats
    // call that fails (a 500, a WAF block) leaves isDev untouched, and deciding
    // once on that would deny a dev for the rest of the page's life.
    apiClient?.onProfileUpdated?.(settle);
    // Resolves immediately once the profile is loaded and rejects when there is
    // no session at all; either way the flag is knowable by the time it settles.
    Promise.resolve(apiClient?.updateProfile?.()).then(settle, settle);
    return () => {
      cancelled = true;
      apiClient?.offProfileUpdated?.(settle);
    };
  }, [apiClient]);

  /**
   * Re-use a stored code by proving it against the server, not by trusting it.
   *
   * The stored code is only a convenience — it still has to survive a real
   * request before the viewer renders, so an expired or tampered-with one shows
   * the prompt instead of a viewer whose every call then 403s.
   */
  useEffect(() => {
    let cancelled = false;
    const stored = readStoredCode(email);
    if (!stored) return undefined;
    apiClient.setReplayToken(stored);
    apiClient
      .searchReplays({ limit: 1 })
      .then((result) => {
        if (cancelled) return;
        if (result && result.error) {
          clearStoredCode();
          apiClient.setReplayToken(null);
          setStatus('Your viewer session expired — enter a new code.');
          return;
        }
        setUnlocked(true);
      })
      .catch(() => {
        if (cancelled) return;
        clearStoredCode();
        apiClient.setReplayToken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, email]);


  /** Exchange the authenticator code for a viewer token. */
  const verify = useCallback(
    async (event) => {
      event.preventDefault();
      setStatus('Checking…');
      try {
        const result = await apiClient.unlockReplayViewer(code.trim());
        if (!result || result.error || !result.token) {
          setStatus(result?.error || 'That code was not accepted.');
          return;
        }
        apiClient.setReplayToken(result.token);
        writeStoredCode(email, result.token);
        setCode('');
        setStatus('');
        setUnlocked(true);
      } catch (e) {
        setStatus(`Verification failed: ${e.message}`);
      }
    },
    [apiClient, email, code],
  );

  if (isDev === null) {
    return (
      <Container className="mt-4" fluid>
        <Card className="shadow">
          <CardBody>Checking access…</CardBody>
        </Card>
      </Container>
    );
  }

  if (!isDev) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Card className="shadow">
            <CardBody>Dev access required.</CardBody>
          </Card>
        </Container>
      </>
    );
  }

  if (unlocked) return children;

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Card className="shadow">
          <CardHeader className="border-0">
            <h3 className="mb-0">Session replays — verification required</h3>
            <small className="text-muted">
              Replays are unmasked recordings of patient charts. Enter your
              authenticator code once; it is exchanged for a token that unlocks
              the viewer for 12 hours.
            </small>
          </CardHeader>
          <CardBody>
            <Form onSubmit={verify}>
              <FormGroup>
                <Label>Code</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code"
                  style={{ maxWidth: 200 }}
                />
              </FormGroup>
              <Button color="primary" size="sm" type="submit" disabled={!code.trim()}>
                Unlock
              </Button>
            </Form>
            {status ? <div className="mt-2 text-muted" style={{ fontSize: 13 }}>{status}</div> : null}
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default ReplayAccessGate;
